/**
 * Client-side JavaScript: Filter Bar & Multi-Dimensional Filter Logic
 *
 * Renders a filter bar with search + status chips + filter icon on the main bar.
 * Type and File filters render inside a separate filter drawer.
 * Manages filter state and applies filtering via re-rendering.
 * OR logic within dimensions, AND logic across dimensions, plus text search.
 */

export const JS_FILTERS = `
var _filterState={status:{},type:{},specFile:{},visual:{},evidence:{}};

/* Evidence filters: one predicate per chip, so API and mobile runs share the
   mechanism and a chip only appears when something in the run matches it. */
var EVIDENCE_FILTERS=[
  {v:'api-error',label:'4xx/5xx seen',test:function(t){
    var m=t.apiStatusMix;
    if(!m)return false;
    return (m['4xx']||0)+(m['5xx']||0)+(m['error']||0)>0;
  }},
  {v:'assert-failed',label:'Failed assertion',test:function(t){return (t.assertionsFailed||0)>0;}},
  {v:'crash',label:'Crash logged',test:function(t){return !!t.hasCrashLog;}},
  {v:'device-error',label:'Device log errors',test:function(t){return (t.deviceLogErrors||0)>0;}},
  {v:'video',label:'Has recording',test:function(t){return !!t.hasVideo||!!(t.artifacts&&t.artifacts.video);}},
  {v:'ai-defect',label:'AI defects',test:function(t){return (t.aiDefectCount||0)>0;}}
];

function _evidenceCounts(){
  var counts={};
  for(var f=0;f<EVIDENCE_FILTERS.length;f++)counts[EVIDENCE_FILTERS[f].v]=0;
  for(var i=0;i<tests.length;i++){
    for(var j=0;j<EVIDENCE_FILTERS.length;j++){
      if(EVIDENCE_FILTERS[j].test(tests[i]))counts[EVIDENCE_FILTERS[j].v]++;
    }
  }
  return counts;
}

/* Visual counts reach the grid differently in the two report modes: the
   embedded report inlines the comparisons on each test, while the streaming
   index carries only counts so the grid never has to open a detail file.
   Both are read here so the filter works the same either way. */
function _visCount(t){return t.visual?t.visual.length:(t.visualCount||0);}
function _visFailed(t){
  if(t.visual){var n=0;for(var i=0;i<t.visual.length;i++)if(t.visual[i].status==='failed')n++;return n;}
  return t.visualFailedCount||0;
}
var _totalTests=0;
var _originalSummary=null;

function renderFilterBar(){
  var filterEl=document.getElementById('filter-bar');
  var drawerBodyEl=document.getElementById('filter-drawer-body');
  var statusCounts={passed:0,failed:0,flaky:0,skipped:0,timedout:0};
  var typeCounts={},specCounts={};
  var visualCounts={has:0,failed:0};
  for(var i=0;i<tests.length;i++){
    var t=tests[i];
    if(statusCounts[t.status]!==undefined)statusCounts[t.status]++;
    var tp=t.testType||'unknown';
    typeCounts[tp]=(typeCounts[tp]||0)+1;
    if(t.filePath)specCounts[t.filePath]=(specCounts[t.filePath]||0)+1;
    if(_visCount(t)>0){visualCounts.has++;if(_visFailed(t)>0)visualCounts.failed++;}
  }
  var hasStatus=Object.keys(_filterState.status).length>0;
  var hasType=Object.keys(_filterState.type).length>0;
  var hasSpec=Object.keys(_filterState.specFile).length>0;
  var hasVisual=Object.keys(_filterState.visual).length>0;
  var hasEvidenceSel=Object.keys(_filterState.evidence).length>0;
  var hasAny=hasStatus||hasType||hasSpec||hasVisual||hasEvidenceSel||!!searchQuery;
  var hasAdvanced=hasType||hasSpec||hasVisual||hasEvidenceSel;

  /* ── Filter Bar (search + status chips + filter icon) ── */
  var h='';

  /* Search box (left-aligned, wider) */
  h+='<div class="search-box"><svg class="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg><input class="search-input" id="search-input" type="text" placeholder="Search tests..." value="'+esc(searchQuery)+'"></div>';

  /* Status chips (inline) */
  h+='<div class="filter-chips">';
  var statuses=['passed','failed','flaky','skipped','timedout'];
  var statusLabels={passed:'Passed',failed:'Failed',flaky:'Flaky',skipped:'Skipped',timedout:'Timed Out'};
  for(var i=0;i<statuses.length;i++){
    var s=statuses[i];var cnt=statusCounts[s]||0;
    var cls='filter-chip'+(!!_filterState.status[s]?' active':'')+(cnt===0?' filter-chip--dimmed':'');
    h+='<button class="'+cls+'" data-dimension="status" data-value="'+s+'">'+statusLabels[s]+' <span class="chip-count">'+cnt+'</span></button>';
  }
  h+='</div>';

  /* Filter icon button */
  h+='<button class="filter-icon-btn" title="More filters">';
  h+='<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z"/></svg>';
  if(hasAdvanced)h+='<span class="filter-icon-badge"></span>';
  h+='</button>';

  if(filterEl)filterEl.innerHTML=h;

  /* ── Filter Drawer Body (type + file + actions) ── */
  var d='';

  /* Type section */
  var types=Object.keys(typeCounts).sort();
  if(types.length>0){
    d+='<div class="filter-drawer-section"><span class="filter-drawer-section-label">Type</span>';
    d+='<div class="filter-chips">';
    for(var i=0;i<types.length;i++){
      var tp=types[i];var cnt=typeCounts[tp]||0;
      var cls='filter-chip'+(!!_filterState.type[tp]?' active':'')+(cnt===0?' filter-chip--dimmed':'');
      d+='<button class="'+cls+'" data-dimension="type" data-value="'+esc(tp)+'">'+esc(tp)+' <span class="chip-count">'+cnt+'</span></button>';
    }
    d+='</div></div>';
  }

  /* Spec file section */
  var specs=Object.keys(specCounts).sort();
  if(specs.length>1){
    d+='<div class="filter-drawer-section"><span class="filter-drawer-section-label">File</span>';
    d+='<div class="filter-chips">';
    for(var i=0;i<specs.length;i++){
      var sp=specs[i];var cnt=specCounts[sp]||0;
      var short=sp.split('/').pop()||sp;
      var cls='filter-chip'+(!!_filterState.specFile[sp]?' active':'')+(cnt===0?' filter-chip--dimmed':'');
      d+='<button class="'+cls+'" data-dimension="specFile" data-value="'+esc(sp)+'" title="'+esc(sp)+'">'+esc(short)+' <span class="chip-count">'+cnt+'</span></button>';
    }
    d+='</div></div>';
  }

  /* Visual section — only when the run actually produced comparisons */
  if(visualCounts.has>0){
    d+='<div class="filter-drawer-section"><span class="filter-drawer-section-label">Visual</span>';
    d+='<div class="filter-chips">';
    var visOpts=[{v:'has',label:'Has visual',cnt:visualCounts.has},{v:'failed',label:'Visual failed',cnt:visualCounts.failed}];
    for(var vi=0;vi<visOpts.length;vi++){
      var vo=visOpts[vi];
      var vcls='filter-chip'+(!!_filterState.visual[vo.v]?' active':'')+(vo.cnt===0?' filter-chip--dimmed':'');
      d+='<button class="'+vcls+'" data-dimension="visual" data-value="'+vo.v+'">'+vo.label+' <span class="chip-count">'+vo.cnt+'</span></button>';
    }
    d+='</div></div>';
  }

  /* Evidence section — a chip per predicate that anything in the run meets */
  var evCounts=_evidenceCounts();
  var evAny=false;
  for(var ek in evCounts){if(evCounts.hasOwnProperty(ek)&&evCounts[ek]>0)evAny=true;}
  if(evAny){
    d+='<div class="filter-drawer-section"><span class="filter-drawer-section-label">Evidence</span>';
    d+='<div class="filter-chips">';
    for(var ei=0;ei<EVIDENCE_FILTERS.length;ei++){
      var ef=EVIDENCE_FILTERS[ei],ecnt=evCounts[ef.v];
      if(ecnt===0)continue;
      var ecls='filter-chip'+(!!_filterState.evidence[ef.v]?' active':'');
      d+='<button class="'+ecls+'" data-dimension="evidence" data-value="'+ef.v+'">'+ef.label+' <span class="chip-count">'+ecnt+'</span></button>';
    }
    d+='</div></div>';
  }

  /* Actions (Clear all + indicator) */
  d+='<div class="filter-drawer-actions">';
  d+='<button class="filter-clear" id="filter-clear" style="display:'+(hasAny?'inline-block':'none')+'">Clear all</button>';
  d+='<span class="filter-indicator" id="filter-indicator" style="display:'+(hasAny?'inline':'none')+'">';
  d+='Showing <span id="filter-shown">0</span> of <span id="filter-total">'+_totalTests+'</span> tests</span>';
  d+='</div>';

  if(drawerBodyEl)drawerBodyEl.innerHTML=d;
}

function initFilters(){
  _filterState={status:{},type:{},specFile:{},visual:{},evidence:{}};
  _totalTests=tests.length;
  _originalSummary={total:data.summary.total,passed:data.summary.passed,
    failed:data.summary.failed,flaky:data.summary.flaky,
    skipped:data.summary.skipped,timedout:data.summary.timedout||0};
}

function getFilteredTests(){
  var hasStatus=Object.keys(_filterState.status).length>0;
  var hasType=Object.keys(_filterState.type).length>0;
  var hasSpec=Object.keys(_filterState.specFile).length>0;
  var hasVisual=Object.keys(_filterState.visual).length>0;
  var hasEvidence=Object.keys(_filterState.evidence).length>0;
  var out=[];
  for(var i=0;i<tests.length;i++){
    var t=tests[i];
    if(hasStatus&&!_filterState.status[t.status])continue;
    if(hasType&&!_filterState.type[t.testType||'unknown'])continue;
    if(hasSpec&&!_filterState.specFile[t.filePath])continue;
    if(hasVisual){
      /* OR within the dimension, matching every other filter here. */
      var visOk=(_filterState.visual.has&&_visCount(t)>0)||(_filterState.visual.failed&&_visFailed(t)>0);
      if(!visOk)continue;
    }
    if(hasEvidence){
      var evOk=false;
      for(var ef2=0;ef2<EVIDENCE_FILTERS.length;ef2++){
        var cand=EVIDENCE_FILTERS[ef2];
        if(_filterState.evidence[cand.v]&&cand.test(t)){evOk=true;break;}
      }
      if(!evOk)continue;
    }
    if(searchQuery){
      var q=searchQuery.toLowerCase();
      var match=t.title.toLowerCase().indexOf(q)>=0||t.filePath.toLowerCase().indexOf(q)>=0||(t.suiteName&&t.suiteName.toLowerCase().indexOf(q)>=0);
      if(!match&&t.tags){for(var ti=0;ti<t.tags.length;ti++){if(t.tags[ti]&&t.tags[ti].toLowerCase().indexOf(q)>=0){match=true;break}}}
      if(!match)continue;
    }
    out.push(t);
  }
  return out;
}

function toggleFilter(dim,val){
  if(!_filterState[dim])_filterState[dim]={};
  if(_filterState[dim][val]){
    delete _filterState[dim][val];
  }else{
    _filterState[dim][val]=true;
  }
  applyFilters();
}

function clearAllFilters(){
  for(var dim in _filterState){if(_filterState.hasOwnProperty(dim))_filterState[dim]={};}
  searchQuery='';
  var si=document.getElementById('search-input');
  if(si)si.value='';
  applyFilters();
}

function applyFilters(){
  renderFilterBar();
  renderTestGrid();
  var hasStatus=Object.keys(_filterState.status).length>0;
  var hasType=Object.keys(_filterState.type).length>0;
  var hasSpec=Object.keys(_filterState.specFile).length>0;
  var hasAny=hasStatus||hasType||hasSpec||!!searchQuery;
  if(hasAny){
    var filtered=getFilteredTests();
    var byStatus={passed:0,failed:0,flaky:0,skipped:0,timedout:0};
    for(var i=0;i<filtered.length;i++){
      var st=filtered[i].status;
      if(byStatus[st]!==undefined)byStatus[st]++;
    }
    _updateCard('s-total',filtered.length);
    _updateCard('s-passed',byStatus.passed);
    _updateCard('s-failed',byStatus.failed);
    _updateCard('s-flaky',byStatus.flaky);
    _updateCard('s-skipped',byStatus.skipped);
    _updateCard('s-timedout',byStatus.timedout);
    var shown=document.getElementById('filter-shown');
    if(shown)shown.textContent=''+filtered.length;
  }else if(_originalSummary){
    _updateCard('s-total',_originalSummary.total);
    _updateCard('s-passed',_originalSummary.passed);
    _updateCard('s-failed',_originalSummary.failed);
    _updateCard('s-flaky',_originalSummary.flaky);
    _updateCard('s-skipped',_originalSummary.skipped);
    _updateCard('s-timedout',_originalSummary.timedout);
  }
}

function _updateCard(cls,val){
  var el=document.querySelector('.'+cls+' .s-count');
  if(el)el.textContent=''+val;
}
`;
